import { Controller } from "@hotwired/stimulus"
import { renderPriceChart } from '../utils/price_chart_renderer'
import { stageCssCode } from '../utils/chart_utils'

export default class extends Controller {
  static targets = ["modal", "chart", "title", "asOfInfo", "stageBadge", "attribution"]
  static values = {
    url: { type: String, default: "/forum/chart_data" },
    logScale: { type: Boolean, default: false },
    maConfig: { type: Array, default: [] },
    chartType: { type: String, default: "candle" },
    upColor: { type: String, default: "" },
    downColor: { type: String, default: "" }
  }

  open(event) {
    // Modified clicks keep their browser meaning (open-in-new-tab/window) —
    // the /leaders chips are real links to the stock profile page.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return

    event.preventDefault()

    const ticker = event.currentTarget.dataset.ticker
    const asOf = event.currentTarget.dataset.asOf
    if (!ticker) return

    // Update modal title
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `$${ticker}`
    }

    // Show as-of date info
    if (this.hasAsOfInfoTarget) {
      if (asOf) {
        this.asOfInfoTarget.textContent = `Chart data as of ${asOf}`
        this.asOfInfoTarget.classList.remove("d-none")
      } else {
        this.asOfInfoTarget.classList.add("d-none")
      }
    }

    if (this.hasStageBadgeTarget) this.stageBadgeTarget.classList.add("d-none")
    this.renderAttribution(null)

    // Show loading state in chart container
    if (this.hasChartTarget) {
      this.chartTarget.innerHTML = '<div class="d-flex justify-content-center align-items-center h-100"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div></div>'
    }

    // Open the modal
    const modal = new bootstrap.Modal(this.modalTarget)
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.fetchAndRender(ticker, asOf)
    }, { once: true })
    modal.show()
  }

  fetchAndRender(ticker, asOf) {
    let url = `${this.urlValue}?ticker=${encodeURIComponent(ticker)}`
    if (asOf) {
      url += `&as_of=${encodeURIComponent(asOf)}`
    }

    // A slow response from an earlier chip must not overwrite the chart the
    // user is looking at now — only the latest request may render.
    const requestSeq = (this.requestSeq = (this.requestSeq || 0) + 1)

    fetch(url)
      .then(response => {
        // A gated endpoint answers an expired session / lapsed subscription
        // with an HTML redirect, which fetch follows to a 200 page — surface
        // that instead of the JSON parser's "Unexpected token '<'".
        if (response.redirected) {
          throw new Error("Your session has changed — reload the page to continue")
        }
        if (response.status === 404) throw new Error(`${ticker} not found`)
        // A paywalled ticker (a coin, for a member below its tier) answers a
        // JSON 403 whose `error` is the sentence to show.
        if (response.status === 403) {
          return response.json().then(body => { throw new Error(body.error || "Not available on your plan") })
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      })
      .then(response => {
        if (requestSeq !== this.requestSeq) return

        if (!response.data || response.data.length === 0) {
          this.chartTarget.innerHTML = `<div class="d-flex justify-content-center align-items-center h-100 text-muted">No chart data available for ${this.escapeHtml(ticker)}</div>`
          return
        }
        this.renderChart(response.data, response.stock, response.stages)
        this.updateStageBadge(response.current_stage)
        this.renderAttribution(response.attribution)
      })
      .catch(error => {
        if (requestSeq !== this.requestSeq) return
        this.chartTarget.innerHTML = `<div class="d-flex justify-content-center align-items-center h-100 text-danger">${this.escapeHtml(error.message)}</div>`
      })
  }

  // Licence attribution for a crypto chart, from the payload; hidden for
  // the ASX. Built with textContent/href, never innerHTML.
  renderAttribution(attribution) {
    if (!this.hasAttributionTarget) return

    this.attributionTarget.replaceChildren()
    if (!attribution) {
      this.attributionTarget.classList.add("d-none")
      return
    }

    const link = document.createElement("a")
    link.textContent = attribution.text
    link.href = attribution.url
    link.target = "_blank"
    link.rel = "noopener"
    link.className = "small text-muted"
    this.attributionTarget.appendChild(link)
    this.attributionTarget.classList.remove("d-none")
  }

  renderChart(data, stockInfo, stages = null) {
    this.destroyChart()

    // Shared with the forum's inline [chart:TICKER] embeds — see
    // utils/price_chart_renderer.
    this.handle = renderPriceChart(this.chartTarget, data, stockInfo, {
      logScale: this.logScaleValue,
      maConfig: this.maConfigValue,
      chartType: this.chartTypeValue,
      upColor: this.upColorValue || null,
      downColor: this.downColorValue || null,
      stages
    })
  }

  // Current Weinstein stage chip in the modal header (the /leaders popup) —
  // code/label/key all come from the server payload so the display metadata
  // stays single-sourced in LeadersHelper::STAGE_META.
  updateStageBadge(currentStage) {
    if (!this.hasStageBadgeTarget) return

    if (!currentStage) {
      this.stageBadgeTarget.classList.add("d-none")
      return
    }

    const code = stageCssCode(currentStage.key)
    this.stageBadgeTarget.textContent = `${currentStage.code} · ${currentStage.label}`
    this.stageBadgeTarget.className = `stage-chip stage-chip--${code} ms-2`
  }

  destroyChart() {
    if (this.handle) {
      this.handle.destroy()
      this.handle = null
    }
  }

  disconnect() {
    this.destroyChart()
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
